import { Injectable, inject } from '@angular/core';
import { Observable, catchError, defer, map, of, shareReplay, tap } from 'rxjs';
import { IBoondockingListing, IListing, IPrivateListing } from './mock-listings.data';
import { LiveSearchService } from './live-search.service';
import { IPoi } from '../pois/poi.types';

export interface IListingCatalog {
  listings: IPrivateListing[];
  boondocking: IBoondockingListing[];
  pois: IPoi[];
}

/**
 * Shared, session-cached view of the live search backend's full listing/boondocking/POI
 * catalog. Every guest-facing surface that lists or searches stays sources from here,
 * so the search HTTP call fires once per session (shareReplay) rather than once per
 * consumer.
 */
@Injectable({ providedIn: 'root' })
export class ListingCatalogService {
  private liveSearch = inject(LiveSearchService);

  /** Set once the full catalog has loaded, so later private-listing requests can reuse it. */
  private resolvedCatalog: IListingCatalog | null = null;

  private readonly catalog$: Observable<IListingCatalog> = this.liveSearch.findAll().pipe(
    catchError(() => of({ listings: [], boondocking: [], pois: [] } as IListingCatalog)),
    tap((catalog) => (this.resolvedCatalog = catalog)),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  private readonly privateListings$: Observable<IPrivateListing[]> = this.liveSearch.findPrivateListings().pipe(
    catchError(() => of([] as IPrivateListing[])),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  getCatalog$(): Observable<IListingCatalog> {
    return this.catalog$;
  }

  /** Private listings only. Reuses the full catalog when it has already loaded;
   *  otherwise makes (and shares) the single listings request, skipping the
   *  boondocking and multi-MB POI requests the full catalog needs. */
  getPrivateListings$(): Observable<IPrivateListing[]> {
    return defer(() => (this.resolvedCatalog ? of(this.resolvedCatalog.listings) : this.privateListings$));
  }

  findListing$(id: number): Observable<IListing | undefined> {
    return this.catalog$.pipe(map((c) => [...c.listings, ...c.boondocking].find((l) => l.id === id)));
  }
}
